import React, { useState, useEffect, useRef } from 'react'
import { ProcedureTypeService } from '../../services/procedureTypeService'
import { ProcedureCategoryService } from '../../services/procedureCategoryService'
import type { ProcedureType, CreateProcedureTypeRequest, UpdateProcedureTypeRequest } from '../../services/procedureTypeService'
import type { ProcedureCategory } from '../../services/procedureCategoryService'

interface ProcedureTypeDetailPageProps {
  procedureTypeCode?: string
  onNavigate: (page: string, data?: any) => void
}

interface FormData {
  procedureTypeCode: string
  procedureName: string
  description: string
  categoryCode: string
  displayOrder: number
  active: boolean
}

export function ProcedureTypeDetailPage({ procedureTypeCode, onNavigate }: ProcedureTypeDetailPageProps) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [procedureType, setProcedureType] = useState<ProcedureType | null>(null)
  const [categories, setCategories] = useState<ProcedureCategory[]>([])
  const [formData, setFormData] = useState<FormData>({
    procedureTypeCode: '',
    procedureName: '',
    description: '',
    categoryCode: '',
    displayOrder: 0,
    active: true
  })

  // Delete confirmation states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  // Update confirmation states
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false)

  // Create confirmation states
  const [showCreateConfirm, setShowCreateConfirm] = useState(false)

  // Refs
  const procedureTypeCodeInputRef = useRef<HTMLInputElement>(null)
  const procedureNameInputRef = useRef<HTMLInputElement>(null)
  const categorySelectRef = useRef<HTMLSelectElement>(null)

  const isNewType = !procedureTypeCode

  useEffect(() => {
    loadCategories()
    if (procedureTypeCode) {
      loadProcedureType()
    }
  }, [procedureTypeCode])

  const loadCategories = async () => {
    try {
      const data = await ProcedureCategoryService.getAll({ active: true })
      setCategories(data.items)
    } catch (err) {
      console.error('Failed to load categories:', err)
      setError('カテゴリマスタの取得に失敗しました')
    }
  }

  const loadProcedureType = async () => {
    if (!procedureTypeCode) return

    try {
      setLoading(true)
      setError(null)
      const data = await ProcedureTypeService.getByCode(procedureTypeCode)
      setProcedureType(data)
      setFormData({
        procedureTypeCode: data.procedureTypeCode,
        procedureName: data.procedureName,
        description: data.description || '',
        categoryCode: data.categoryCode || '',
        displayOrder: data.displayOrder,
        active: data.active
      })
    } catch (err) {
      console.error('Failed to load procedure type:', err)
      setError('申請種別の取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field: keyof FormData, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const validateForm = (): boolean => {
    if (!formData.procedureTypeCode) {
      if (procedureTypeCodeInputRef.current) {
        procedureTypeCodeInputRef.current.setCustomValidity('申請種別コードを入力してください')
        procedureTypeCodeInputRef.current.reportValidity()
      }
      return false
    }

    if (!formData.procedureName) {
      if (procedureNameInputRef.current) {
        procedureNameInputRef.current.setCustomValidity('申請種別名を入力してください')
        procedureNameInputRef.current.reportValidity()
      }
      return false
    }

    if (!formData.categoryCode) {
      if (categorySelectRef.current) {
        categorySelectRef.current.setCustomValidity('カテゴリを選択してください')
        categorySelectRef.current.reportValidity()
      }
      return false
    }

    return true
  }

  const handleSaveButtonClick = () => {
    if (!validateForm()) {
      return
    }

    if (isNewType) {
      setShowCreateConfirm(true)
    } else {
      setShowUpdateConfirm(true)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)

      if (isNewType) {
        const request: CreateProcedureTypeRequest = {
          procedureTypeCode: formData.procedureTypeCode,
          procedureName: formData.procedureName,
          description: formData.description || undefined,
          categoryCode: formData.categoryCode,
          active: formData.active,
          displayOrder: formData.displayOrder
        }
        await ProcedureTypeService.create(request)
        setShowCreateConfirm(false)
      } else if (procedureType) {
        const request: UpdateProcedureTypeRequest = {
          procedureName: formData.procedureName,
          description: formData.description || undefined,
          categoryCode: formData.categoryCode,
          active: formData.active,
          displayOrder: formData.displayOrder,
          version: procedureType.version
        }
        await ProcedureTypeService.update(procedureType.procedureTypeCode, request)
        setShowUpdateConfirm(false)
      }

      // 保存成功後、一覧へ戻る
      onNavigate('procedure-types')
    } catch (err) {
      console.error('Save failed:', err)
      setError(isNewType ? '作成に失敗しました' : '更新に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!procedureType) return

    try {
      setDeleteLoading(true)
      setError(null)
      await ProcedureTypeService.delete(procedureType.procedureTypeCode)
      // 削除成功後、一覧へ戻る
      onNavigate('procedure-types')
    } catch (err) {
      console.error('Delete failed:', err)
      setError('削除に失敗しました')
      setShowDeleteConfirm(false)
    } finally {
      setDeleteLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center text-gray-500">読み込み中...</div>
      </div>
    )
  }

  return (
    <div className="px-8 py-6">
      <div className="page-header">
        <button
          onClick={() => onNavigate('procedure-types')}
          className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
          style={{ marginBottom: '1.5rem' }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          一覧に戻る
        </button>
        <div style={{
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '0.75rem',
          marginBottom: '2rem'
        }}>
          <h1 className="page-title">
            {isNewType ? '新規申請種別作成' : '申請種別詳細'}
          </h1>
        </div>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #e5e7eb',
          color: '#b91c1c',
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          {error}
        </div>
      )}

      {/* アクション */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">アクション</h2>
        <div className="flex gap-4">
          <button
            onClick={handleSaveButtonClick}
            disabled={saving}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              border: '1px solid #2563eb',
              borderRadius: '0.375rem',
              color: 'white',
              backgroundColor: '#2563eb',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.5 : 1,
              transition: 'all 0.15s'
            }}
            onMouseEnter={(e) => {
              if (!saving) {
                e.currentTarget.style.backgroundColor = '#1d4ed8'
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#2563eb'
            }}
          >
            {saving ? '保存中...' : '保存'}
          </button>
          {!isNewType && procedureType && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="btn"
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                border: '1px solid #dc2626',
                borderRadius: '0.375rem',
                color: '#dc2626',
                backgroundColor: 'white',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#fee2e2'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white'
              }}
            >
              削除
            </button>
          )}
        </div>
      </div>

      {/* 基本情報 */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.3rem' }}>
            <h2 className="text-lg font-semibold text-gray-900">基本情報</h2>
            {!isNewType && procedureType && (
              <span style={{
                padding: '0.25rem 0.75rem',
                fontSize: '0.75rem',
                fontWeight: 600,
                borderRadius: '0.375rem',
                backgroundColor: procedureType.active ? '#dcfce7' : '#f3f4f6',
                color: procedureType.active ? '#166534' : '#374151'
              }}>
                {procedureType.active ? '有効' : '無効'}
              </span>
            )}
          </div>
        </div>
        <div className="space-y-6">
          {/* 申請種別コード */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              申請種別コード <span className="text-red-600">*</span>
            </label>
            <input
              ref={procedureTypeCodeInputRef}
              type="text"
              value={formData.procedureTypeCode}
              onChange={(e) => {
                handleChange('procedureTypeCode', e.target.value)
                if (procedureTypeCodeInputRef.current) {
                  procedureTypeCodeInputRef.current.setCustomValidity('')
                }
              }}
              disabled={!isNewType}
              placeholder="E2E_TEST_PROC など"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
            <p className="text-xs text-gray-500 mt-1">
              {isNewType ? '英数字とアンダースコアで入力' : '※コードは変更できません'}
            </p>
          </div>

          {/* 申請種別名 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              申請種別名 <span className="text-red-600">*</span>
            </label>
            <input
              ref={procedureNameInputRef}
              type="text"
              value={formData.procedureName}
              onChange={(e) => {
                handleChange('procedureName', e.target.value)
                if (procedureNameInputRef.current) {
                  procedureNameInputRef.current.setCustomValidity('')
                }
              }}
              placeholder="有給休暇申請"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              ユーザーに表示される名称
            </p>
          </div>

          {/* 説明 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              説明（任意）
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="この申請種別の説明を記載"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* カテゴリ */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              カテゴリ <span className="text-red-600">*</span>
            </label>
            <select
              ref={categorySelectRef}
              value={formData.categoryCode}
              onChange={(e) => {
                handleChange('categoryCode', e.target.value)
                if (categorySelectRef.current) {
                  categorySelectRef.current.setCustomValidity('')
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- カテゴリを選択 --</option>
              {categories.map((cat) => (
                <option key={cat.categoryCode} value={cat.categoryCode}>
                  {cat.categoryName}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              申請種別を分類するカテゴリ
            </p>
          </div>

          {/* 表示順 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              表示順
            </label>
            <input
              type="number"
              value={formData.displayOrder}
              onChange={(e) => handleChange('displayOrder', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{
                border: '1px solid #d1d5db',
                borderColor: '#d1d5db'
              }}
            />
            <p className="text-xs text-gray-500 mt-1">
              小さい値ほど上位に表示されます
            </p>
          </div>

          {/* ステータス */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ステータス
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => handleChange('active', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
              />
              <span className="text-sm font-medium text-gray-700">有効</span>
            </label>
            <p className="text-xs text-gray-500 mt-1">
              無効にすると業務画面から選択できなくなります
            </p>
          </div>
        </div>
      </div>

      {/* Create Confirmation Dialog */}
      {showCreateConfirm && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">作成確認</h2>
            <p className="text-gray-700 mb-2">
              以下の申請種別を作成してもよろしいですか？
            </p>
            <div className="bg-gray-50 p-3 rounded mb-4">
              <div className="text-sm">
                <div><strong>コード:</strong> {formData.procedureTypeCode}</div>
                <div><strong>名称:</strong> {formData.procedureName}</div>
                <div><strong>カテゴリ:</strong> {categories.find(c => c.categoryCode === formData.categoryCode)?.categoryName || formData.categoryCode}</div>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateConfirm(false)}
                className="btn btn-secondary"
                disabled={saving}
              >
                キャンセル
              </button>
              <button
                onClick={handleSave}
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? '作成中...' : '作成'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Confirmation Dialog */}
      {showUpdateConfirm && procedureType && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">更新確認</h2>
            <p className="text-gray-700 mb-2">
              以下の申請種別を更新してもよろしいですか？
            </p>
            <div className="bg-gray-50 p-3 rounded mb-4">
              <div className="text-sm">
                <div><strong>コード:</strong> {procedureType.procedureTypeCode}</div>
                <div><strong>名称:</strong> {formData.procedureName}</div>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowUpdateConfirm(false)}
                className="btn btn-secondary"
                disabled={saving}
              >
                キャンセル
              </button>
              <button
                onClick={handleSave}
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? '更新中...' : '更新'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && procedureType && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">削除確認</h2>
            <p className="text-gray-700 mb-2">
              以下の申請種別を削除してもよろしいですか？
            </p>
            <div className="bg-gray-50 p-3 rounded mb-4">
              <div className="text-sm">
                <div><strong>コード:</strong> {procedureType.procedureTypeCode}</div>
                <div><strong>名称:</strong> {procedureType.procedureName}</div>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn-secondary"
                disabled={deleteLoading}
              >
                キャンセル
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
                disabled={deleteLoading}
              >
                {deleteLoading ? '削除中...' : '削除'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
